import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePossibleNumberWithReason,
  ValidationResult,
} from 'lib-shared-phone-numbers';
import { User, UserType } from 'lib-shared-types-api';
import { USER_ROLE_ATTRIBUTES, UserRole } from 'lib-shared-user-role';
import isEmail from 'validator/lib/isEmail';

import { numberFormatterE164 } from 'app/utils/telephonyHelpers';

import { UserModalFieldAlert } from './components/fieldAlert/userModalFieldAlert';
import {
  validatePhoneNumber as validateE164PhoneNumberApi,
  validateEmail as validateEmailApi,
} from './userModal.api';
import type {
  ArrayFormField,
  UserModalAsyncValidationField,
  UserModalFormData,
  UserModalFormErrors,
} from './userModal.types';
import {
  AddUserPayload,
  UpdateUserPayload,
  UserModalConflictingIdResponse,
  UserModalValidationResponses,
} from './userModalApi.types';

export const scimTooltipContent = (
  <>
    You cannot edit this field because the information is coming from another
    system.
  </>
);

export const userTypeTooltipContent = (
  <>
    <strong>Contact</strong>
    <p>
      Contacts are created automatically when a customer contacts you by phone,
      chat or email. A Contact can also be added manually.
    </p>
    <strong>Agent</strong>
    <p>
      Agents are the people who work in Dixa and have a Role assigned to them.
      Please be aware that you will be charged with each new agent based on your
      contract. If you have any questions, just reach out to your account
      manager for additional details.
    </p>
  </>
);

export const asyncValidationFieldNameMap: Record<
  UserModalAsyncValidationField,
  'Email' | 'Phone number'
> = {
  emails: 'Email',
  phone_numbers: 'Phone number',
};

function validatePhoneNumberApi(phoneNumber: string) {
  return validateE164PhoneNumberApi(
    // Replace with e164 format.
    numberFormatterE164({
      phoneNumber: phoneNumber.replace(/\s/g, ''),
    }),
  );
}

const asyncValidationApiMap: Record<
  UserModalAsyncValidationField,
  (value: string) => Promise<UserModalValidationResponses>
> = {
  emails: validateEmailApi,
  phone_numbers: validatePhoneNumberApi,
};

function getPhoneNumberValidationError(
  phoneNumber: string,
): string | undefined {
  if (
    phoneNumber.trim() !== '' ||
    isPossiblePhoneNumber(phoneNumber) === false ||
    isValidPhoneNumber(phoneNumber) === false
  ) {
    switch (validatePossibleNumberWithReason(phoneNumber)) {
      case ValidationResult.INVALID_COUNTRY_CODE:
        return `Invalid country code`;
      case ValidationResult.TOO_SHORT:
        return `Too short`;
      case ValidationResult.TOO_LONG:
        return `Too long`;
    }
  }
}

function getRequiredOrMinimumLengthError(
  value?: string | null,
  minLength = 2,
): string | undefined {
  if (!value?.trim()) {
    return 'Required';
  }

  if (value.trim().length < minLength) {
    return 'Must be 2 characters or more';
  }
}

export const validate = (values: UserModalFormData): UserModalFormErrors => {
  values.emails = values.emails ?? [];
  values.phone_numbers = values.phone_numbers ?? [];

  const errors: UserModalFormErrors = {
    name: getRequiredOrMinimumLengthError(values.name),
  };

  const primaryEmailIdx = values.primary_email_idx ?? 0;
  const primaryPhoneIdx = values.primary_phone_idx ?? 0;
  const primaryEmail = values.emails[primaryEmailIdx]?.value?.trim();
  const primaryPhone = values.phone_numbers[primaryPhoneIdx]?.value?.trim();
  errors.emails = Array(values.emails.length).fill({});
  errors.phone_numbers = Array(values.phone_numbers.length).fill({});

  switch (values.user_type) {
    case 'contact': {
      if (!primaryEmail && !primaryPhone) {
        const value = 'Either email or phone number is required';
        errors.emails[primaryEmailIdx] = { value };
        errors.phone_numbers[primaryPhoneIdx] = { value };
      }
      break;
    }

    case 'member': {
      errors.given_name = getRequiredOrMinimumLengthError(values.given_name);
      errors.family_name = getRequiredOrMinimumLengthError(values.family_name);

      if (!primaryEmail) {
        errors.emails[primaryEmailIdx] = { value: 'Required' };
      }
      if (!values.role) {
        errors.role = 'Required';
      }
      if (!values.custom_roles?.length) {
        errors.custom_roles = 'Required';
      }
      break;
    }

    case 'bot':
    default:
      break;
  }

  validateContactPoints(
    values.emails,
    errors.emails,
    primaryEmailIdx,
    'emails',
  );
  validateContactPoints(
    values.phone_numbers,
    errors.phone_numbers,
    primaryPhoneIdx,
    'phone_numbers',
  );

  return errors;
};

const isValidContactPoint = (
  value: string | undefined,
  type: UserModalAsyncValidationField,
) => {
  if (!value) return '';

  if (type === 'emails') {
    return !isEmail(value) ? `Invalid email` : '';
  }

  return getPhoneNumberValidationError(value as string);
};

function validateContactPoints(
  values: ArrayFormField,
  errors: ArrayFormField,
  primaryIdx: number,
  type: UserModalAsyncValidationField,
) {
  const hash = {};

  for (let i = 0; i < values.length; i++) {
    const value = values[i].value;
    const error =
      // If already has error - use it and skip other checks
      errors[i].value ||
      // If non-empty and not a valid contact point
      isValidContactPoint(value, type) ||
      // If same contact point was already entered in previous fields
      (value && hash[value] ? 'Cannot be a duplicate' : '');

    // Handle case for endusers if email OR phone number is defined (so the other one is allowed to be empty),
    // and only additional email/phone number was added without primary, for the other contact point.
    // Additional only (without primary) emails / phone numbers are not allowed.
    if (
      i !== primaryIdx &&
      !values[primaryIdx].value &&
      value &&
      !errors[primaryIdx]?.value
    ) {
      errors[primaryIdx] = {
        value: `Primary ${
          'emails' ? 'email' : 'phone number'
        } should be defined`,
      };
    }

    // If not empty preserve value in hash to check for duplicates
    if (value) hash[value] = true;
    errors[i] = { value: error };
  }
}

let asyncValidateTimeout: ReturnType<typeof setTimeout> | null = null;
const asyncValidateDelay = 1500; // Ajax validation doesn't need to be that quick
export const asyncValidate = (values: UserModalFormData): Promise<void> => {
  if (asyncValidateTimeout) {
    clearTimeout(asyncValidateTimeout);
  }

  return new Promise((resolve, reject) => {
    asyncValidateTimeout = setTimeout(async () => {
      const errorResponses = await Promise.all(
        Object.keys(asyncValidationFieldNameMap).flatMap((k) => {
          const key = k as UserModalAsyncValidationField;
          return (values[key] ?? []).map(({ value }) => {
            if (!value) {
              return Promise.resolve({ data: null, key });
            }

            // Do not send additional requests if sync validation fails.
            // redux-form seem to run async validation before sync.
            // See for details - https://github.com/redux-form/redux-form/issues/3944
            const error = isValidContactPoint(value, key);
            if (error) {
              return Promise.resolve({ data: null, key });
            }

            return asyncValidationApiMap[key](value).then(({ data }) => ({
              data,
              key,
            }));
          });
        }),
      );

      const errors = errorResponses.reduce(
        (errors, { data, key }) => {
          errors[key] = errors[key] ?? [];
          // Set null to preserve order in array, with null being 'no error for this particular element in array'.
          const maybeError =
            data && !data.available && data.conflicting_id !== values.id
              ? {
                  value: (
                    <UserModalFieldAlert
                      field={asyncValidationFieldNameMap[key]}
                      userId={data.conflicting_id}
                    />
                  ),
                }
              : null;

          errors[key].push(maybeError);
          errors.hasError = errors.hasError || !!maybeError;

          return errors;
        },
        { hasError: false },
      );

      if (errors.hasError) {
        return reject(errors);
      }

      return resolve();
    }, asyncValidateDelay);
  });
};

export const buildNewUser = ({
  values,
}: {
  values: UserModalFormData;
}): AddUserPayload => {
  const primaryEmailIdx = values.primary_email_idx ?? 0;
  const primaryPhoneIdx = values.primary_phone_idx ?? 0;

  const newUser: AddUserPayload = {
    name: values.name ?? null,
    user_type: values.user_type,
    roles: getRolesBasedOnUserType(values.user_type, values.role),
    channels: ['email', 'pstn_phone', 'widget_chat'],
    given_name: null,
    family_name: null,
    email: null,
    phone_number: null,
    company_id: null,
    avatar_url: null,
  };

  if (typeof values.given_name === 'string' && values.given_name?.trim()) {
    newUser.given_name = values.given_name;
  }
  if (typeof values.family_name === 'string' && values.family_name?.trim()) {
    newUser.family_name = values.family_name;
  }
  const email = values.emails?.[primaryEmailIdx].value?.trim();
  if (email && typeof email === 'string') {
    newUser.email = email;
  }

  const phoneNumber = values.phone_numbers?.[primaryPhoneIdx].value?.trim();
  if (phoneNumber && typeof phoneNumber === 'string') {
    // Use E.164 format.
    newUser.phone_number = numberFormatterE164({ phoneNumber });
  }

  if (
    values.company_id &&
    typeof values.company_id === 'string' &&
    values.company_id.trim() !== ''
  ) {
    newUser.company_id = values.company_id;
  }

  if (values.avatar_url) {
    newUser.avatar_url = values.avatar_url;
  }

  const additionalEmails = values.emails
    // Omit primary email
    ?.map(({ value }, i) => (i !== primaryEmailIdx ? value?.trim() : null))
    .filter((x): x is string => !!x);

  if (additionalEmails?.length) {
    newUser.additional_emails = additionalEmails;
  }

  const additionalPhoneNumbers = values.phone_numbers
    ?.map(({ value }, i) => {
      // Omit primary phone number
      if (i === primaryPhoneIdx) return null;

      const phoneNumber = value?.trim();
      return phoneNumber ? numberFormatterE164({ phoneNumber }) : phoneNumber;
    })
    .filter((x): x is string => !!x);

  if (additionalPhoneNumbers?.length) {
    newUser.additional_phone_numbers = additionalPhoneNumbers;
  }

  if (values.custom_roles?.length) {
    newUser.customRoles = values.custom_roles;
  }

  return newUser;
};

export const buildUpdateUser = ({
  values,
  user,
}: {
  values: UserModalFormData;
  user: User;
}): UpdateUserPayload => {
  const primaryEmailIdx = values.primary_email_idx ?? 0;
  const primaryPhoneIdx = values.primary_phone_idx ?? 0;
  const email = values.emails?.[primaryEmailIdx].value?.trim();
  const phoneNumber = values.phone_numbers?.[primaryPhoneIdx].value?.trim();

  const patch: Required<UpdateUserPayload['patch']> = {
    updates: {
      email,
      display_name: values.name,
      phone_number: phoneNumber ? numberFormatterE164({ phoneNumber }) : '',
      company_id: values.company_id,
      user_type: values.user_type,
      roles: getRolesBasedOnUserType(values.user_type, values.role),
      additional_emails: values.emails
        // Omit primary email
        ?.map(({ value }, i) => (i !== primaryEmailIdx ? value?.trim() : null))
        .filter((x): x is string => !!x),
      additional_phone_numbers: values.phone_numbers
        ?.map(({ value }, i) => {
          // Omit primary phone number
          if (i === primaryPhoneIdx) return null;

          const phoneNumber = value?.trim();
          return phoneNumber
            ? numberFormatterE164({ phoneNumber })
            : phoneNumber;
        })
        .filter((x): x is string => !!x),
    },
    deletes: [],
  };
  if (typeof values.given_name === 'string' && values.given_name?.trim()) {
    patch.updates.given_name = values.given_name;
  }
  if (typeof values.family_name === 'string' && values.family_name?.trim()) {
    patch.updates.family_name = values.family_name;
  }
  if (!patch.updates.phone_number) {
    delete patch.updates.phone_number;
    patch.deletes.push('phone_number');
  }
  if (!email) {
    delete patch.updates.email;
    patch.deletes.push('email');
  }
  if (
    patch.updates.company_id === null ||
    values.company_id === '' ||
    values.company_id === undefined
  ) {
    delete patch.updates.company_id;
    patch.deletes.push('company_id');
  }
  if (patch.updates.roles === null || !values.role) {
    delete patch.updates.roles;
  }
  // Remove email from patch if it is the same as on init.
  if (email === user.email) {
    delete patch.updates.email;
  }
  // Remove phone number from patch if it is the same as on init.
  if (phoneNumber === user.phone_number) {
    delete patch.updates.phone_number;
  }

  const additionalEmails = patch.updates.additional_emails ?? [];
  // Remove additional_emails from patch if it is the same as on init.
  if (
    additionalEmails.length &&
    additionalEmails.length === user.additional_emails?.length &&
    additionalEmails.every((e) => user.additional_emails?.includes(e))
  ) {
    delete patch.updates.additional_emails;
  }

  const additionalPhoneNumbers = patch.updates.additional_phone_numbers ?? [];
  // Remove additional_phone_numbers from patch if it is the same as on init.
  if (
    additionalPhoneNumbers.length &&
    additionalPhoneNumbers.length === user.additional_phone_numbers?.length &&
    additionalPhoneNumbers.every((e) =>
      user.additional_phone_numbers?.includes(e),
    )
  ) {
    delete patch.updates.additional_phone_numbers;
  }

  if (!additionalEmails.length && user.additional_emails?.length) {
    delete patch.updates.additional_emails;
    patch.deletes.push('additional_emails');
  }

  if (!additionalPhoneNumbers.length && user.additional_phone_numbers?.length) {
    delete patch.updates.additional_phone_numbers;
    patch.deletes.push('additional_phone_numbers');
  }

  if (user.avatar_url?.trim() && values.avatar_url === null) {
    patch.deletes.push('avatar_url');
  } else if (user.avatar_url?.trim() !== values.avatar_url?.trim()) {
    patch.updates.avatar_url = values.avatar_url;
  }

  return {
    userId: user.id,
    patch,
    ...(values.custom_roles?.length && { customRoles: values.custom_roles }),
  };
};

function getRolesBasedOnUserType(
  userType?: UserType,
  role?: UserRole | null,
): UserRole[] {
  if (!userType) return [];

  switch (userType) {
    case 'bot':
      return [];
    case 'contact':
      return USER_ROLE_ATTRIBUTES['enduser'].roles;
    case 'member':
      return role ? USER_ROLE_ATTRIBUTES[role].roles : [];
  }
}

export function getUserTypeBasedOnRoles(_roles: UserRole[]): UserType {
  const roles = new Set(_roles.map((role) => role.toLowerCase()));

  if (roles.has('systemuser')) return 'bot';
  if (roles.has('enduser')) return 'contact';
  if (roles.has('agent')) return 'member';

  return 'contact';
}

export function getAvatarUser(user: User | null, formValues: User): User {
  return {
    ...user,
    ...(formValues as User),
    name: formValues?.name ?? user?.name ?? null,
  };
}

/** Checks if a payload has conflicting Id */
export function hasConflictingId(
  payload?: unknown,
): payload is UserModalConflictingIdResponse {
  return !!(payload as UserModalConflictingIdResponse | undefined)
    ?.conflicting_id;
}
